import React from "react";
import { Box, Container, Divider, Link, Stack, Typography, Grid } from "@mui/material";
import { useCart } from "../../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { PATH_NAME } from "../../../configs/pathName";
import colorConfigs from "../../../configs/colorConfigs";
import { typo } from "../../../assets/fonts/font";
import CustomButton from "../../common/Button/CustomButton";
import { ICartItem } from "../../../models/ICart";
import CartDrawerItem from "./CartDrawerItem";
import Calendar from "./deliveryDateTimePicker";

const CartPage = () => {
  const {
    cart,
    removeFromCart,
    updateCartItemQuantity,
    totalPrice,
    freeShippingBarPercentage,
    freeShippingText,
  } = useCart();
  const navigate = useNavigate();
  const [deliveryDate, setDeliveryDate] = React.useState<string | null>(null);
  
  const handleReduceItemQuantity = async (item: ICartItem) => {
    if (!item) return;
    updateCartItemQuantity({
      sku: item.sku_sku,
      quantity: item.quantity - 1,
    });
  };

  const handleIncreaseItemQuantity = async (item: ICartItem) => {
    if (!item) return;
    updateCartItemQuantity({
      sku: item.sku_sku,
      quantity: item.quantity + 1,
    });
  };

  const handleRemoveItem = async (item: ICartItem) => {
    if (!item) return;
    removeFromCart(item);
  };

  const handleCheckout = () => {
    navigate(PATH_NAME.CHECKOUT);
  };
  

  const handleDateSelect = (date: string, time: string) => {
    setDeliveryDate(date);
  };

  return (
    <Box sx={{ background: colorConfigs.secondary }}>
      <Container sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ ...typo.title }} gutterBottom>
              Your Cart
            </Typography>
            <Link
              href="#"
              sx={{ 
                ...typo.bodyContent,
                display: "block", 
                mb: 2,
                textDecoration: "underline",
                color: colorConfigs.tertiary,
                "&:hover": {
                  color: colorConfigs.primary,
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              Continue shopping
            </Link>
            <Divider
              sx={{
                my: 1,
                borderColor: colorConfigs.primary,
                opacity: 0.5,
                borderWidth: "1px",
              }}
            />
          </Box>

          {cart?.items?.length === 0 ? (
            <Typography sx={{ ...typo.title }} textAlign="center" py={4}>
              Your cart is empty
            </Typography>
          ) : (
            <>
              {/* Product List - Using CartDrawerItem */}
              <Box
                sx={{
                  padding: "1rem",
                  border: "1px solid var(--color-black)",
                  borderRadius: "1rem",
                  backgroundColor: "var(--color-white)",
                }}
              >
                <Stack spacing={2}>
                  {cart?.items?.map((item) => (
                    <CartDrawerItem
                      key={item.sku_sku}
                      item={item}
                      removeItem={handleRemoveItem}
                      reduceItemQuantity={handleReduceItemQuantity}
                      increaseItemQuantity={handleIncreaseItemQuantity}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Free Shipping Progress */}
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "8px",
                  width: "100%",
                  height: "0.5rem",
                  backgroundColor: "var(--color-muted-background)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    backgroundColor: colorConfigs.primary,
                    width: `${freeShippingBarPercentage}%`,
                    height: "100%",
                    transition: "all 0.3s ease",
                  }}
                ></Box>
              </Box>
              <Typography sx={{ ...typo.bodyContent, textAlign: "center" }}>
                {freeShippingText}
              </Typography>
              <Calendar onDateTimeSelect={handleDateSelect} />

              {/* Delivery Information */}
              <Box
                sx={{
                  padding: "1rem",
                  border: "1px solid var(--color-black)",
                  borderRadius: "1rem",
                  backgroundColor: "var(--color-white)",
                }}
              >
                <Typography sx={{ ...typo.title }} gutterBottom>
                  Delivery Information
                </Typography>
                <Typography sx={{ ...typo.bodyContent }} paragraph>
                  While we'll do our best to meet your preferred delivery time,
                  please note that factors like traffic and location allocation may
                  affect delivery schedules. We appreciate your understanding in
                  unforeseen circumstances, and we will deliver your order within
                  the stipulated time range for your order.
                </Typography>
                <Typography sx={{ ...typo.bodyContent }} paragraph>
                  Delivery slots from 9am-6pm the next day are only applicable for orders placed
                  before 2.30pm. For orders affected, we will have to contact you
                  separately to reschedule.
                </Typography>
              </Box>

              {/* Subtotal and Checkout */}
              <Box
                sx={{
                  padding: "1rem",
                  border: "1px solid var(--color-black)",
                  borderRadius: "1rem",
                  backgroundColor: "var(--color-white)",
                }}
              >
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography sx={{ ...typo.title }}>Subtotal</Typography>
                    <Typography sx={{ ...typo.bodyContent }}>
                      Taxes and shipping calculated at checkout
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography sx={{ ...typo.title, textDecoration: "underline" }}>
                      ${totalPrice?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{
                    width: "100%",
                    justifyContent: "flex-end",
                    mt: 3,
                  }}
                >
                  <CustomButton
                    buttonType="secondary"
                    onClick={() => navigate(PATH_NAME.ALLPRODUCTS)}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Continue Shopping
                  </CustomButton>
                  
                  
                    <CustomButton
                      buttonType="primary"
                      onClick={handleCheckout}
                      disabled={cart?.items?.length === 0 || !deliveryDate}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      Checkout
                    </CustomButton>
                  
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default CartPage;